import React from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa6";
import { IoRestaurant } from "react-icons/io5";
import { useLanguage } from "../../../contexts/LanguageContext";

const SocialIcon = ({ href, label, children, className = "" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white hover:bg-orange-500 transition-colors ${className}`}
    aria-label={label}
  >
    {children}
  </a>
);

/**
 * Footer visiteur modernise : structure claire, visuel epure.
 */
const FooterSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();

  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-12 sm:mt-16 lg:mt-20 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-white/10 bg-slate-950/70 backdrop-blur-md shadow-[0_18px_55px_-30px_rgba(0,0,0,0.65)] p-6 sm:p-8 lg:p-10 text-white">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 lg:gap-10">
            <div className="space-y-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-white hover:opacity-90 transition-opacity"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-700/80 border border-slate-500/40 flex items-center justify-center">
                  <IoRestaurant className="w-5 h-5" />
                </div>
                <span className="font-bold text-lg">JesCook</span>
              </Link>
              <p className="text-white/80 text-sm leading-relaxed max-w-xs">
                JesCook rassemble les cuisines du monde pour decouvrir, partager et
                savourer.
              </p>
              <div>
                <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-white/75 mb-3">
                  {t("stayConnected")}
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <SocialIcon href="https://facebook.com" label="Facebook">
                    <FaFacebookF className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://instagram.com" label="Instagram">
                    <FaInstagram className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://twitter.com" label="Twitter">
                    <FaXTwitter className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://linkedin.com" label="LinkedIn">
                    <FaLinkedinIn className="w-4 h-4" />
                  </SocialIcon>
                  <SocialIcon href="https://youtube.com" label="YouTube">
                    <FaYoutube className="w-4 h-4" />
                  </SocialIcon>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-white/75 mb-4">
                Navigation
              </h3>
              <ul className="space-y-2.5 text-sm text-white/80">
                <li>
                  <button
                    type="button"
                    onClick={onViewRecipes}
                    className="hover:text-white transition-colors text-left"
                  >
                    {t("recipes")}
                  </button>
                </li>
                <li>
                  <Link to="/auth" className="hover:text-white transition-colors">
                    {t("login")}
                  </Link>
                </li>
                <li>
                  <Link to="/favorites" className="hover:text-white transition-colors">
                    {t("favorites")}
                  </Link>
                </li>
                <li>
                  <Link to="/recipes" className="hover:text-white transition-colors">
                    {t("worldMenus")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-white/75 mb-4">
                Informations
              </h3>
              <ul className="space-y-2.5 text-sm text-white/80">
                <li>
                  <a href="#mentions" className="hover:text-white transition-colors">
                    {t("legal")}
                  </a>
                </li>
                <li>
                  <a
                    href="#confidentialite"
                    className="hover:text-white transition-colors"
                  >
                    {t("privacy")}
                  </a>
                </li>
                <li>
                  <a href="#cgu" className="hover:text-white transition-colors">
                    {t("terms")}
                  </a>
                </li>
                <li>
                  <a href="#cookies" className="hover:text-white transition-colors">
                    {t("cookies")}
                  </a>
                </li>
                <li>
                  <a href="#carrieres" className="hover:text-white transition-colors">
                    {t("careers")}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase tracking-[0.12em] text-white/75 mb-4">
                {t("contact")}
              </h3>
              <ul className="space-y-3 text-sm text-white/80">
                <li className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-white/55">
                    {t("address")}
                  </p>
                  <p>{t("addressValue")}</p>
                </li>
                <li className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-white/55">
                    {t("email")}
                  </p>
                  <a
                    href={`mailto:${t("emailValue")}`}
                    className="hover:text-white transition-colors break-all"
                  >
                    {t("emailValue")}
                  </a>
                </li>
                <li className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.1em] text-white/55">
                    {t("phone")}
                  </p>
                  <a
                    href={`tel:${t("phoneValue").replace(/\s/g, "")}`}
                    className="hover:text-white transition-colors"
                  >
                    {t("phoneValue")}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 sm:mt-10 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-white/70 text-sm">
            <p>
              © {currentYear} JesCook. {t("allRightsReserved")}.
            </p>
            <p className="text-xs">
              {t("madeWith")} ❤️ {t("byPassionate")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
